/* Page score — the admin spike surface.

   Deliberately plain and deliberately temporary. It exists to answer one
   question before anything is built on top of it: do the two passes in
   worker/pagescore.ts produce output worth a real feature? So it shows the raw
   result — the quoted generic lines, the source behind every finding, the
   mechanical facts — because a score you cannot check is a score nobody will
   trust.

   The headline number is effort+originality, NOT the rater's overall: across
   real pages, skill tracks the copywriter and accuracy defaults to
   "unverified", so both sit near-constant across a site and only compress the
   spread the list has to sort on.

   Nothing is stored. Running it twice costs two model calls and changes
   nothing. */
import { useState } from 'react';
import { Button, Card, Input } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { ScoreDetail } from '@/components/ScoreDetail';
import { scorePage, type PageScoreResult, type ConflictSide } from '@/data/client';

const OK = 'var(--ok, #15803d)';
const WARN = 'var(--warn, #b45309)';
const BAD = 'var(--danger, #b91c1c)';

/* The bands the rubric itself describes, so a colour means what the prompt
   means rather than being a second opinion about the same number. */
function band(n: number): { label: string; color: string } {
  if (n >= 7) return { label: 'Specific', color: OK };
  if (n >= 4) return { label: 'Competent', color: WARN };
  return { label: 'Generic', color: BAD };
}

const ROUTE_LABEL: Record<string, string> = {
  faq: 'Ask as an FAQ',
  interview: 'Needs an interview',
  live: 'Worth a live conversation',
};

const sideLabel = (s: ConflictSide) => (s ? (s.kind === 'page' ? 'this page' : `${s.title || s.id}`) : 'an unknown source');

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 10, fontSize: 13, padding: '3px 0' }}>
      <span className="dash-muted">{label}</span>
      <span>{children}</span>
    </div>
  );
}

const Mark = ({ on, good = true }: { on: boolean; good?: boolean }) => (
  <span style={{ color: on === good ? OK : WARN, fontWeight: 600 }}>{on ? 'yes' : 'no'}</span>
);

function Section({ title, count, children }: { title: string; count?: number; children: React.ReactNode }) {
  return (
    <div>
      <p style={{ fontWeight: 600, margin: '0 0 6px', fontSize: 13 }}>
        {title}{count === undefined ? '' : ` (${count})`}
      </p>
      {children}
    </div>
  );
}

export function PageScore() {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [res, setRes] = useState<PageScoreResult | null>(null);
  const [showFacts, setShowFacts] = useState(false);

  const run = async () => {
    const u = url.trim();
    if (!u) return;
    setBusy(true); setErr(''); setRes(null);
    try {
      setRes(await scorePage({ url: u }));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  };

  const rank = res?.rank ?? 0;
  const f = res?.facts;

  return (
    <Card padding={false} className="dash-leads">
      <div className="dash-panelhead dash-panelhead--pad">
        <div>
          <h2><Icon name="sparkles" size={16} /> Page score <span className="dash-muted">(one page)</span></h2>
          <p>
            Score any page now, without scanning the whole site. It joins Page health above, so the
            findings are still there after an interview or an FAQ comes back.
            {' '}<span className="dash-muted" style={{ fontSize: 11 }}>
              build {new Date(__BUILD_TIME__).toLocaleString()}
            </span>
          </p>
        </div>
      </div>

      <div style={{ padding: '0 16px 16px', display: 'grid', gap: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 320px' }}>
            <Input
              label="Page URL"
              placeholder="https://example.com/services/mold-remediation/"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !busy) run(); }}
            />
          </div>
          <Button variant="primary" size="sm" onClick={run} disabled={busy || !url.trim()}>
            {busy ? 'Scoring…' : 'Run'}
          </Button>
        </div>

        {err && <p className="dash-muted" style={{ color: BAD }}>{err}</p>}
        {busy && <p className="dash-muted">Fetching the page, then two passes — about 20–40 seconds.</p>}

        {res?.saved && (
          /* Rather than repeating the worksheet's actions here. Reading
             findings and acting on them is one task, and two screens that both
             half-do it is how somebody ends up working the wrong copy. */
          <p style={{ fontSize: 13, margin: 0, color: OK }}>
            <Icon name="check" size={13} /> Saved to Page health — open it above to send questions or write the additions.
          </p>
        )}
        {res && !res.saved && (
          <p className="dash-muted" style={{ fontSize: 12, margin: 0 }}>
            Not saved: pasted copy with no page path has nothing to file it under. Give a URL, or add the
            path, and it joins Page health.
          </p>
        )}

        {res && f && (
          <div style={{ display: 'grid', gap: 18 }}>
            <p className="dash-muted" style={{ margin: 0 }}>
              {res.page.title || res.page.route} — {f.wordCount.toLocaleString()} words ·
              {' '}{res.sourcesUsed} Vault source{res.sourcesUsed === 1 ? '' : 's'} ·
              {' '}{res.faqsOnPage} FAQ{res.faqsOnPage === 1 ? '' : 's'} on this page
            </p>

            {res.effort ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
                  <span style={{ fontSize: 34, fontWeight: 700, color: band(rank).color, lineHeight: 1 }}>{rank}</span>
                  <span className="dash-muted">/ 10 — {band(rank).label}</span>
                </div>
                <p className="dash-muted" style={{ margin: '0 0 10px', fontSize: 12 }}>
                  Effort and originality only. Rater's overall was {res.effort.scores.overall}.
                </p>

                {/* One rendering for both screens — see ScoreDetail. Two copies
                    of the same stored reading drift, and then somebody argues
                    with a number using a screen that is not showing them the
                    same thing. Open here, because checking one page IS the
                    reasoning; on the worksheet it is collapsed behind the work. */}
                <ScoreDetail effort={res.effort} facts={f} proof={res.proof}
                  accuracyConcern={res.accuracyConcern} defaultOpen />

                {/* Only meaningful because the rater was never told the target. */}
                {(res.topic.inferred || res.topic.target) && (
                  <div style={{ marginTop: 12 }}>
                    <Row label="Reads as">{res.topic.inferred || '—'}</Row>
                    <Row label="Target">{res.topic.target || <span className="dash-muted">none on record</span>}</Row>
                  </div>
                )}
              </div>
            ) : (
              <p className="dash-muted">No effort score came back — the rater's answer could not be read.</p>
            )}

            <Section title="Proof on this page">
              <Row label="Own photos">
                <strong style={{ color: res.proof.photos.onPage ? OK : WARN }}>{res.proof.photos.onPage}</strong>
                {res.proof.photos.available > 0 && (
                  <span className="dash-muted"> · {res.proof.photos.available} in Expert Studio for this service</span>
                )}
              </Row>
              {res.proof.photos.stock > 0 && (
                <Row label="Stock photos">
                  <strong style={{ color: WARN }}>{res.proof.photos.stock}</strong>
                  <span className="dash-muted"> · a contractor page is the wrong place for these</span>
                </Row>
              )}
              <Row label="Reviews quoted">
                <strong style={{ color: res.proof.reviews.quoted ? OK : WARN }}>{res.proof.reviews.quoted}</strong>
                <span className="dash-muted">
                  {' '}· {res.proof.reviews.matching} of {res.proof.reviews.available} mention
                  {' '}{res.proof.reviews.terms.length
                    ? res.proof.reviews.terms.map((t) => `“${t}”`).join(', ')
                    : 'this service'}
                </span>
              </Row>
            </Section>

            {/* Contradictions first: a company disagreeing with itself in public
                outranks anything it merely left out. */}
            {res.coverage.conflicts.length > 0 && (
              <Section title="Contradictions" count={res.coverage.conflicts.length}>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {res.coverage.conflicts.map((cf, i) => (
                    <li key={i} style={{ fontSize: 13, margin: '6px 0' }}>
                      <span style={{ color: BAD, fontWeight: 600 }}>{cf.what}</span>
                      <div className="dash-muted" style={{ fontSize: 12 }}>
                        between {sideLabel(cf.a)} and {sideLabel(cf.b)}
                      </div>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            <Section title="In the Vault, not on this page" count={res.coverage.missing.length}>
              {res.coverage.missing.length === 0
                ? <p className="dash-muted" style={{ fontSize: 13, margin: 0 }}>Nothing — this page spends what's on record.</p>
                : (
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {res.coverage.missing.map((m, i) => (
                      <li key={i} style={{ fontSize: 13, margin: '6px 0' }}>
                        {m.what}
                        {m.why && <span className="dash-muted"> — {m.why}</span>}
                        {/* The citation is the point: a finding you cannot trace
                            back to a source is one nobody should act on. */}
                        <div className="dash-muted" style={{ fontSize: 12 }}>
                          from {m.source ? `[${m.source.kind}] ${m.source.title || m.source.id}` : '(source not resolved)'}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
            </Section>

            <Section title="Nobody has answered these" count={res.coverage.unanswered.length}>
              {res.coverage.unanswered.length === 0
                ? <p className="dash-muted" style={{ fontSize: 13, margin: 0 }}>Nothing outstanding.</p>
                : (
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {res.coverage.unanswered.map((u, i) => (
                      <li key={i} style={{ fontSize: 13, margin: '6px 0' }}>
                        {u.question}
                        <span className="dash-muted"> — {ROUTE_LABEL[u.route] ?? u.route}</span>
                      </li>
                    ))}
                  </ul>
                )}
            </Section>

            {/* Measured, not judged — no model was asked about any of this. */}
            <div>
              <Button variant="ghost" size="sm" onClick={() => setShowFacts((v) => !v)}>
                {showFacts ? 'Hide' : 'Show'} what's on the page
              </Button>
              {showFacts && (
                <div style={{ marginTop: 8 }}>
                  {f.canonicalOffHost && (
                    <p style={{ color: BAD, fontSize: 13, fontWeight: 600, margin: '0 0 8px' }}>
                      Canonical points at another host: {f.canonical}
                    </p>
                  )}
                  <Row label="Byline"><Mark on={f.byline} /></Row>
                  <Row label="Schema">
                    {f.schemaTypes.length ? f.schemaTypes.join(', ') : <span className="dash-muted">none found</span>}
                  </Row>
                  <Row label="Business schema"><Mark on={f.hasLocalBusinessSchema} /></Row>
                  <Row label="Breadcrumbs"><Mark on={f.hasBreadcrumbSchema} /></Row>
                  <Row label="Images">
                    {f.images.total} total · {f.images.own} own · {f.images.stock} stock · {f.images.withAlt} with alt
                  </Row>
                  <Row label="Links">
                    {f.links.internal} internal · {f.links.external} external · {f.links.authority} to .gov/.edu
                  </Row>
                  <Row label="Question headings">{f.questionHeadings} of {f.h2.length} H2s</Row>
                  <Row label="Specificity">{f.numbers} numbers · {f.numbersPer1k} per 1,000 words</Row>
                  <Row label="Trust markers">
                    licensed <Mark on={f.trust.licensed} /> · insured <Mark on={f.trust.insured} /> ·
                    {' '}bonded <Mark on={f.trust.bonded} /> · warranty <Mark on={f.trust.warranty} />
                  </Row>
                  {f.trust.certifications.length > 0 && (
                    <Row label="Named brands">{f.trust.certifications.join(', ')}</Row>
                  )}
                  {f.keyword && (
                    <Row label={`"${f.keyword.target}"`}>
                      title <Mark on={f.keyword.inTitle} /> · H1 <Mark on={f.keyword.inH1} /> ·
                      {' '}first 100 words <Mark on={f.keyword.inFirst100} /> · an H2 <Mark on={f.keyword.inAnyH2} />
                    </Row>
                  )}
                  <Row label="Title">{f.titleLength} chars</Row>
                  <Row label="Meta description">
                    {f.metaDescription ? `${f.metaDescription.length} chars` : <span style={{ color: WARN }}>missing</span>}
                  </Row>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
